import React, { useEffect, useMemo, useState } from "react";
import { createPaleo, getPaleo, updatePaleo } from "../api/dinosApi";
import { useNavigate, useParams } from "react-router-dom";

const COUNTRY_OPTIONS = [
  // América (5)
  { value: "Argentina 🇦🇷", label: "Argentina 🇦🇷" },
  { value: "Brasil 🇧🇷", label: "Brasil 🇧🇷" },
  { value: "México 🇲🇽", label: "México 🇲🇽" },
  { value: "Estados Unidos 🇺🇸", label: "Estados Unidos 🇺🇸" },
  { value: "Chile 🇨🇱", label: "Chile 🇨🇱" },

  // Europa (5)
  { value: "España 🇪🇸", label: "España 🇪🇸" },
  { value: "Francia 🇫🇷", label: "Francia 🇫🇷" },
  { value: "Italia 🇮🇹", label: "Italia 🇮🇹" },
  { value: "Alemania 🇩🇪", label: "Alemania 🇩🇪" },
  { value: "Reino Unido 🇬🇧", label: "Reino Unido 🇬🇧" },

  // Asia (5)
  { value: "Japón 🇯🇵", label: "Japón 🇯🇵" },
  { value: "China 🇨🇳", label: "China 🇨🇳" },
  { value: "India 🇮🇳", label: "India 🇮🇳" },
  { value: "Corea del Sur 🇰🇷", label: "Corea del Sur 🇰🇷" },
  { value: "Tailandia 🇹🇭", label: "Tailandia 🇹🇭" },

  // África (5)
  { value: "Sudáfrica 🇿🇦", label: "Sudáfrica 🇿🇦" },
  { value: "Egipto 🇪🇬", label: "Egipto 🇪🇬" },
  { value: "Marruecos 🇲🇦", label: "Marruecos 🇲🇦" },
  { value: "Nigeria 🇳🇬", label: "Nigeria 🇳🇬" },
  { value: "Kenia 🇰🇪", label: "Kenia 🇰🇪" },

  // Oceanía (5)
  { value: "Australia 🇦🇺", label: "Australia 🇦🇺" },
  { value: "Nueva Zelanda 🇳🇿", label: "Nueva Zelanda 🇳🇿" },
  { value: "Fiyi 🇫🇯", label: "Fiyi 🇫🇯" },
  { value: "Papúa Nueva Guinea 🇵🇬", label: "Papúa Nueva Guinea 🇵🇬" },
  { value: "Samoa 🇼🇸", label: "Samoa 🇼🇸" },
];

const SPECIALTY_OPTIONS = [
  "Paleochoripánología",
  "Estratigrafía del Mate",
  "Fósiles y Quilmesología",
  "Dino-Delivery (Pedidos Ya Jurásico)",
  "Cartografía del Conurbano Cretácico",
  "Restos Óseos & Asado Forense",
  "Rastrosaurus: huellas en vereda rota",
  "Tangoestratigrafía",
  "Taxonomía del Fernet",
  "Sedimentología del Dulce de Leche",
];

const AdminPaleosForm = ({ mode = "create" }) => {
  const { id } = useParams();
  const nav = useNavigate();
  const isEdit = mode === "edit";

  const allowedCountries = useMemo(
    () => new Set(COUNTRY_OPTIONS.map((c) => c.value)),
    []
  );
  const allowedSpecialties = useMemo(() => new Set(SPECIALTY_OPTIONS), []);

  const [form, setForm] = useState({
    name: "",
    country: "",
    specialty: "",
  });

  const [touched, setTouched] = useState({
    name: false,
    country: false,
    specialty: false,
  });

  const [errors, setErrors] = useState({
    name: "",
    country: "",
    specialty: "",
  });

  const [loading, setLoading] = useState(isEdit);
  const [msg, setMsg] = useState(null);

  // ------------ Validaciones --------
  const validateField = (name, value) => {
    const v = (value ?? "").toString().trim();

    if (name === "name") {
      if (!v) return "Debe ingresar un nombre";
      if (v.length < 3) return "El nombre debe tener al menos 3 caracteres";
      if (v.length > 50) return "El nombre no puede superar 50 caracteres";
      // letras, números, espacios y signos
      const ok = /^[\p{L}\p{N} .,'-]+$/u.test(v);
      if (!ok) return "El nombre tiene caracteres no válidos";
      return "";
    }

    if (name === "country") {
      if (!v) return "";
      if (!allowedCountries.has(v)) return "Debe seleccionar un país válido";
      return "";
    }

    if (name === "specialty") {
      if (!v) return "";
      if (!allowedSpecialties.has(v))
        return "Debe seleccionar una especialidad válida";
      return "";
    }

    return "";
  };

  const validateAll = (data) => {
    const next = {
      name: validateField("name", data.name),
      country: validateField("country", data.country),
      specialty: validateField("specialty", data.specialty),
    };
    setErrors(next);
    return !Object.values(next).some(Boolean);
  };

  useEffect(() => {
    const fetchPaleo = async () => {
      if (!isEdit) return;

      try {
        setLoading(true);
        const res = await getPaleo(id);

        const nextForm = {
          name: res.data?.name || "",
          country: res.data?.country || "",
          specialty: res.data?.specialty || "",
        };

        if (nextForm.country && !allowedCountries.has(nextForm.country)) {
          nextForm.country = "";
        }
        if (nextForm.specialty && !allowedSpecialties.has(nextForm.specialty)) {
          nextForm.specialty = "";
        }

        setForm(nextForm);
        validateAll(nextForm);
      } catch (err) {
        console.error(err);
        setMsg("No se pudo cargar el paleontólogo (ID inválido o no existe).");
      } finally {
        setLoading(false);
      }
    };

    fetchPaleo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => {
      const next = { ...prev, [name]: value };
      // validación en el campo
      setErrors((prevErr) => ({
        ...prevErr,
        [name]: touched[name] ? validateField(name, value) : prevErr[name],
      }));
      return next;
    });
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg(null);

    // para que se vean los errores
    setTouched({ name: true, country: true, specialty: true });

    const ok = validateAll(form);
    if (!ok) {
      setMsg("Revisá los campos marcados en rojo.");
      return;
    }

    try {
      if (isEdit) {
        await updatePaleo(id, form);
        setMsg("Paleontólogo actualizado.");
      } else {
        await createPaleo(form);
        setMsg("Paleontólogo creado.");
        setForm({ name: "", country: "", specialty: "" });
        setTouched({ name: false, country: false, specialty: false });
        setErrors({ name: "", country: "", specialty: "" });
      }

      setTimeout(() => nav("/admin/paleos"), 600);
    } catch (err) {
      console.error(err);
      setMsg(err?.response?.data?.message || "Error al guardar paleontólogo.");
    }
  };

  const hasErrors = Object.values(errors).some(Boolean);

  if (loading) return <div className="alert alert-secondary">Cargando...</div>;

  return (
    <div className="container" style={{ maxWidth: 650 }}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="m-0">
          {isEdit ? "Editar Paleontólogo" : "Crear Paleontólogo"}
        </h2>

        <button
          className="btn btn-outline-secondary"
          type="button"
          onClick={() => nav("/admin/paleos")}
        >
          Volver
        </button>
      </div>

      {msg && <div className="alert alert-info">{msg}</div>}

      <form onSubmit={handleSubmit} className="card p-4 shadow-sm" noValidate>
        {/* Nombre */}
        <div className="mb-3">
          <label className="form-label">Nombre *</label>
          <input
            className={`form-control ${
              touched.name && errors.name ? "is-invalid" : ""
            }`}
            name="name"
            value={form.name}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Ej: Ricardo Fortosaurio"
            required
            maxLength={50}
          />
          {touched.name && errors.name && (
            <div className="invalid-feedback d-block">{errors.name}</div>
          )}
        </div>

        {/* País */}
        <div className="mb-3">
          <label className="form-label">País</label>
          <select
            className={`form-select ${
              touched.country && errors.country ? "is-invalid" : ""
            }`}
            name="country"
            value={form.country}
            onChange={handleChange}
            onBlur={handleBlur}
          >
            <option value="">Seleccioná un país</option>
            {COUNTRY_OPTIONS.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
          {touched.country && errors.country && (
            <div className="invalid-feedback d-block">{errors.country}</div>
          )}
        </div>

        {/* Especialidad */}
        <div className="mb-3">
          <label className="form-label">Especialidad</label>
          <select
            className={`form-select ${
              touched.specialty && errors.specialty ? "is-invalid" : ""
            }`}
            name="specialty"
            value={form.specialty}
            onChange={handleChange}
            onBlur={handleBlur}
          >
            <option value="">Elegí una especialidad</option>
            {SPECIALTY_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          {touched.specialty && errors.specialty && (
            <div className="invalid-feedback d-block">{errors.specialty}</div>
          )}
        </div>

        <button className="btn btn-success" disabled={hasErrors}>
          {isEdit ? "Actualizar" : "Crear"}
        </button>
      </form>
    </div>
  );
};

export default AdminPaleosForm;
